import { Capacitor } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

import type {
  AppSettings,
  AuthStatus,
  CatalogItem,
  DictionaryEntry,
  ImportResult,
  Job,
  Talk,
  TalkDetail
} from "../types";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export const AUTH_REQUIRED_EVENT = "ted:auth-required";

function notifyUnauthorized(path: string, status: number) {
  if (status === 401 && path !== "/api/auth/login") {
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(path, {
    ...options,
    headers,
    credentials: "same-origin"
  });
  notifyUnauthorized(path, response.status);
  if (!response.ok) {
    let detail = `请求失败（${response.status}）`;
    try {
      const payload = await response.json();
      detail = payload.detail || detail;
    } catch {
      // Keep the status-based message when the response is not JSON.
    }
    throw new ApiError(detail, response.status);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  authStatus: () => request<AuthStatus>("/api/auth/status"),
  setup: (password: string) =>
    request<AuthStatus>("/api/auth/setup", {
      method: "POST",
      body: JSON.stringify({ password })
    }),
  login: (password: string) =>
    request<AuthStatus>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password })
    }),
  logout: () =>
    request<AuthStatus>("/api/auth/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<AuthStatus>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword })
    }),

  talks: (params?: { q?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.status) query.set("status", params.status);
    const suffix = query.size ? `?${query}` : "";
    return request<Talk[]>(`/api/talks${suffix}`);
  },
  talk: (id: string) => request<TalkDetail>(`/api/talks/${id}`),
  importTalk: (url: string) =>
    request<ImportResult>("/api/talks/import", {
      method: "POST",
      body: JSON.stringify({ url })
    }),
  updateProgress: (id: string, progressMs: number) =>
    request<Talk>(`/api/talks/${id}/progress`, {
      method: "PATCH",
      body: JSON.stringify({ progressMs })
    }),
  latestJob: (id: string) =>
    request<Job | null>(`/api/talks/${id}/latest-job`),
  retryTalk: (id: string) =>
    request<ImportResult>(`/api/talks/${id}/retry`, { method: "POST" }),
  regenerateTalk: (id: string) =>
    request<ImportResult>(`/api/talks/${id}/regenerate`, { method: "POST" }),
  cancelTalk: (id: string) =>
    request<Job>(`/api/talks/${id}/cancel`, { method: "POST" }),
  deleteTalk: (id: string) =>
    request<void>(`/api/talks/${id}`, { method: "DELETE" }),
  patchCue: (
    talkId: string,
    cueId: number,
    payload: Partial<{
      startMs: number;
      endMs: number;
      en: string;
      zh: string;
    }>
  ) =>
    request(`/api/talks/${talkId}/cues/${cueId}`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  splitCue: (talkId: string, cueId: number) =>
    request(`/api/talks/${talkId}/cues/${cueId}/split`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  mergeCue: (talkId: string, cueId: number) =>
    request(`/api/talks/${talkId}/cues/${cueId}/merge-next`, {
      method: "POST"
    }),

  catalog: () => request<CatalogItem[]>("/api/catalog"),
  syncCatalog: () =>
    request<{ synced: number; message: string }>("/api/catalog/sync", {
      method: "POST"
    }),
  addCatalogItem: (id: number) =>
    request<ImportResult>(`/api/catalog/${id}/add`, { method: "POST" }),

  dictionary: (word: string) =>
    request<DictionaryEntry>(`/api/dictionary/${encodeURIComponent(word)}`),
  settings: () => request<AppSettings>("/api/settings")
};

export async function downloadSubtitle(
  talk: Talk,
  format: "srt" | "vtt",
  mode: "bilingual" | "en" | "zh"
) {
  const response = await fetch(
    `/api/talks/${talk.id}/export?fmt=${format}&mode=${mode}`,
    { credentials: "same-origin" }
  );
  notifyUnauthorized(
    `/api/talks/${talk.id}/export?fmt=${format}&mode=${mode}`,
    response.status
  );
  if (!response.ok) {
    throw new ApiError("字幕导出失败", response.status);
  }
  const blob = await response.blob();
  const filename = `ted-${talk.id.slice(0, 8)}-${mode}.${format}`;
  if (Capacitor.isNativePlatform()) {
    const saved = await Filesystem.writeFile({
      path: filename,
      data: await blob.text(),
      directory: Directory.Cache,
      encoding: Encoding.UTF8
    });
    try {
      await Share.share({
        title: filename,
        text: "TED 双语字幕",
        url: saved.uri,
        dialogTitle: "导出字幕"
      });
    } catch (error) {
      if (!(error instanceof Error) || !/cancel/i.test(error.message)) {
        throw error;
      }
    }
    return;
  }
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

